import { Alert, AlertTitle, Typography } from '@mui/material'
import Debug from 'debug'
import { createContext, useContext, useEffect } from 'react'
import { UseQueryExecute, useQuery } from 'urql'
import { graphql } from '../gql'
import { DashboardContextProviderQuery } from '../gql/graphql'
import useEventStream from './EventStream/useEventStream'

const log = Debug('ela:DashboardContextProvider')
log.log = console.info.bind(console)

export type TDashboardContext = {
  fetching: boolean
  error?: Error
  refetch: UseQueryExecute
  recentPayments?: DashboardContextProviderQuery['Payments']
  purchasedRfqs?: DashboardContextProviderQuery['Rfqs']
  outstandingCommission: number
}

export const DashboardContext = createContext<TDashboardContext | undefined>(undefined)

export function DashboardContextProvider({ children }: { children: React.ReactNode }) {
  const [pageQueryResult, refreshPageQuery] = useQuery({
    query: graphql(/* GraphQL */ `
      query DashboardContextProvider($filters: ListPaymentsFilterInput) {
        Payments(filters: $filters) {
          amount
          dateReceived
          dateCreated
          vendor {
            name
          }
        }
        Rfqs(filters: { statuses: [PURCHASED, PAID] }) {
          vendor {
            name
          }
          quote {
            purchaseOrder {
              amount
              commissionPercentage
            }
          }
        }
        OutstandingCommission
      }
    `),
    requestPolicy: 'network-only',
  })

  useEventStream({
    onMessage: (message) => {
      if (message?.event?.startsWith('rfq.updated')) {
        setTimeout(() => refreshPageQuery(), 100)
      }
    },
  })

  const value: TDashboardContext = {
    fetching: pageQueryResult.fetching,
    error: pageQueryResult.error,
    refetch: refreshPageQuery,
    recentPayments: pageQueryResult.data?.Payments,
    purchasedRfqs: pageQueryResult.data?.Rfqs,
    outstandingCommission: Number(pageQueryResult.data?.OutstandingCommission.replace(/[$,]/g, '')) ?? 0,
  }

  useEffect(() => {
    if (value.error) {
      log('Error loading dashboard query', value.error)
    }
  }, [value.error])

  if (value.error) {
    return (
      <div>
        <Typography variant="h1" pb={4}>
          Error loading dashboard
        </Typography>
        <Alert severity="error">
          <AlertTitle>{value.error.message}</AlertTitle>
          <pre>{value.error.stack}</pre>
        </Alert>
      </div>
    )
  }

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>
}

export function useDashboardContext() {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error('useDashboardContext must be used within an DashboardContextProvider')
  }
  return context
}
